import { Box, Backdrop, CircularProgress, Button, Stack, Typography, TextField, useTheme, useMediaQuery, MenuItem, Dialog } from "@mui/material";
import { useState, useContext, useEffect, useMemo } from "react";
import { Scanner } from '@yudiel/react-qr-scanner';
import { Context } from "../App";
import Header from "../components/layout/Header";
import AutoSearch from "../components/ui/AutoCompletSearch";
import ScannedBilletInfo from "../components/ui/ScannedBilletInfo";
import UnScannBillet from "../components/forms/UnScannBillet";
import beepError, {longBeep} from "../components/ui/beep";

export default function QRScan() {
    const {setIsConnected} = useContext(Context)
    const user = JSON.parse(sessionStorage.getItem('user'))

    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

    const [load, setLoad] = useState(true)
    const [scan, setScan] = useState(false)
    const [events, setEvents] = useState([]);
    const [eventId, setEventId] = useState('');
    const [billets, setBillets] = useState([]);
    const [billet, setBillet] = useState();
    
    const [msg, setMsg] = useState('')
    const [open, setOpen] = useState(false)
    const [action, setAction] = useState('')

    const event = useMemo(()=> events.find(s=>s._id===eventId), [eventId, events])

    function handleScan(data){
        setLoad(true); setMsg('')
        const result = data[0]?.rawValue; 
        if (Boolean(result)) {
            console.log('Scanned data:', result);
            ScanBillet(result)
        } else {
            setLoad(false)   
        }
    }

    function ScanBillet(bltNum) {
        const query = new URLSearchParams({num: bltNum}).toString();

        fetch(process.env.REACT_APP_API_URL.concat(`/billets/${eventId}/scan?${query}`), {method:'GET', headers:{
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + sessionStorage.getItem('token')
        }}).then(async response=>{
            const token = response.headers.get('x-auth-token')
            if(response.status === 401) {
                sessionStorage.clear()
                setIsConnected(false)
            }else {
                if (token && token !== sessionStorage.getItem('token') && token !== '') {
                    sessionStorage.setItem('token', response.headers.get('x-auth-token'))
                }
                if (response.status === 200) {
                    const {billet, alreadyScanned} = await response.json()
                    setBillet(billet)
                    if (alreadyScanned) {
                        longBeep();
                        manageDialogue(true, 'unscan')
                    }
                } else {
                    for (let i = 0; i < 3; i++) {
                        beepError();
                        await new Promise((r) => setTimeout(r, 100));
                    } longBeep();
                    const text = await response.text()
                    setMsg(text); setBillet()
                }
                setLoad(false)
            }
        }).catch(error => {
            console.error("Error scanning billet:", error);
            setMsg('Erreur lors du scan du billet. Veuillez réessayer.');
            setLoad(false);
        })
        .finally(() => {
            setLoad(false)
        });
    }

    function manageDialogue(status, action) {
        setOpen(status); setAction(action)
        if (!status) {
            setMsg(''); setBillet();
        }
    }

    function handleSearch(){
        setLoad(true); setMsg(''); setScan(false); setBillet()

        const query = {event: eventId, statut:{$in: ['valide', 'validé']}}

        fetch(process.env.REACT_APP_API_URL.concat('/billets'), {method:'POST', headers:{
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + sessionStorage.getItem('token')
            }, body: JSON.stringify(query)
        }).then(async response=>{
            const token = response.headers.get('x-auth-token')
            if(response.status === 401) {
                sessionStorage.clear()
                setIsConnected(false)
            }else {
                if (token && token !== sessionStorage.getItem('token') && token !== '') {
                    sessionStorage.setItem('token', response.headers.get('x-auth-token'))
                }
                if (response.status === 200) {
                    const json = await response.json()
                    setBillets(json); manageDialogue(true, 'search')
                }
                setLoad(false)
            }
        })
    }

    useEffect(() => {
        if(eventId !== '') {
            setScan(true);
        }
    }, [eventId])

    useEffect(() => {
        if (!user || user.role === 'user') {
            sessionStorage.clear();
            setIsConnected(false);
        }

        if (user.role === 'admin' && user.events.length === 1) {
            setEventId(user.events[0]);
        }

        const query = user.role==='admin' ? {_id: {$in: user.events}} :{};

        fetch(process.env.REACT_APP_API_URL.concat('/events'), {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'authorization': 'Bearer ' + sessionStorage.getItem('token')
            }, body: JSON.stringify(query)
        }).then(async response => {
            const token = response.headers.get('x-auth-token');
            if (response.status === 401) {
                sessionStorage.clear();
                setIsConnected(false);
            } else {
                if (token && token !== sessionStorage.getItem('token') && token !== '') {
                    sessionStorage.setItem('token', response.headers.get('x-auth-token'));
                }
                if (response.status === 200) {
                    const json = await response.json();
                    setEvents(json);
                    if(json.length === 1) setEventId(json[0]._id);
                }
            }
        }).catch(error => {
            console.error("Error fetching events:", error);
        }).finally(() => {
            setLoad(false);
        });
    }, []);


    return(
        <Box>
            <Header/>

            <Box display='flex' flexDirection='column' sx={{flex:1, marginY:'20px', marginX:3}}>

                <Typography variant={isMobile?"h4":"h3"} align="center" gutterBottom>QRcode Scanner</Typography>

                <TextField select fullWidth={isMobile} label="Évènement" sx={{minWidth:'200px', alignSelf:'center'}} value={eventId} slotProps={{ select:{native:true}, inputLabel:{shrink:true} }}
                onChange={(event)=>setEventId(old=>{if(old!==event.target.value){setBillet(); setMsg('')} return event.target.value})}>
                    <option value='' disabled>Sélectionner un évènement</option>
                    {events.map((item, index)=><option key={index} value={item._id}>{item.titre}</option>)}
                </TextField>

                {event&&<Button variant="outlined" sx={{alignSelf:'flex-end', marginTop:2}} onClick={handleSearch}>Recherche Manuelle</Button>}
                
                {!scan&&event&&<Button variant="contained" sx={{alignSelf:'center', marginTop:'100px'}} onClick={()=> {setScan(true); setMsg('');setBillet()}}>Scanner</Button>}
                {scan&&<><Scanner styles={{container:{width:'300px', height:'300px', alignSelf:'center', mt:2}}} scanDelay={500} onScan={handleScan} />
                <Button variant="contained" color="focus" sx={{color:'white', alignSelf:'center', mt:2}} onClick={()=> {setScan(false);setBillet();setMsg('')}}>Stop</Button></>}
                
                {billet&&<ScannedBilletInfo {...{billet}} />}
                
                {!!msg&&<Typography variant="subtitle1" align="center" color="alerte">{msg}</Typography>}
                
                <Backdrop open={load}><CircularProgress color="inherit" /></Backdrop>
            </Box>

            <Dialog open={open} onClose={()=>manageDialogue(false, '')} maxWidth='md' fullWidth>
                {action==='search'&&<Box display='flex' flexDirection='column' sx={{padding:2}}>
                    <AutoSearch {...{data:billets, style:{minWidth:'200px'}, getLabel:item=>item.owner.nom+' '+item.owner.prenom+' - '+item.num, setSelected:setBillet}}/>
                    <Button variant="contained" sx={{alignSelf:'center', marginTop:2}} onClick={()=>{manageDialogue(false, ''); ScanBillet(billet.num)}}>Valider</Button>
                </Box>}
                {action==='unscan'&&<Box display='flex' flexDirection='column' sx={{padding:2}}>
                    <ScannedBilletInfo {...{billet}} />
                    <UnScannBillet {...{billet, close:()=>manageDialogue(false, '')}}/>
                </Box>}
            </Dialog>

        </Box>
    )
}